/**
 * Player-facing lines for the duel event log. Skips engine noise
 * (moves, target prompts, ATK ticks) so the tab stays readable.
 */

import {
  CardPosition,
  EnginePhase,
  EventType,
  type GameEvent,
  type Seat,
} from '@shared/index'
import { localizeDuelMessage } from '../i18n/engineError'

export interface DuelLogLine {
  id: string
  text: string
}

export interface DuelLogCtx {
  t: (key: string, vars?: Record<string, string | number>) => string
  nameOf: (cardId: string | null | undefined) => string
  uidName: (uid: string) => string
  playerName: (seat: Seat) => string
}

let lineSeq = 0

function nextId(): string {
  lineSeq += 1
  return `el-${lineSeq}`
}

function cardOrFallback(
  ctx: DuelLogCtx,
  cardId: string | null | undefined,
  uid?: string,
): string {
  const named = ctx.nameOf(cardId)
  if (named) return named
  if (uid) {
    const fromUid = ctx.uidName(uid)
    if (fromUid) return fromUid
  }
  return ctx.t('duel.log.aCard')
}

function phaseKey(phase: EnginePhase): string {
  switch (phase) {
    case EnginePhase.DRAW:
      return 'duel.phaseBanner.draw'
    case EnginePhase.STANDBY:
      return 'duel.phaseBanner.standby'
    case EnginePhase.MAIN_1:
      return 'duel.phaseBanner.main1'
    case EnginePhase.BATTLE:
      return 'duel.phaseBanner.battle'
    case EnginePhase.MAIN_2:
      return 'duel.phaseBanner.main2'
    case EnginePhase.END:
      return 'duel.phaseBanner.end'
    default:
      return 'duel.phaseBanner.main1'
  }
}

function describeOne(event: GameEvent, ctx: DuelLogCtx): string | null {
  switch (event.type) {
    case EventType.TURN_CHANGED:
      return ctx.t('duel.log.turn', {
        n: event.turnCount,
        name: ctx.playerName(event.turnPlayer),
      })
    case EventType.PHASE_CHANGED:
      return ctx.t('duel.log.phase', {
        name: ctx.playerName(event.turnPlayer),
        phase: ctx.t(phaseKey(event.phase)),
      })
    case EventType.CARD_DRAWN: {
      const who = ctx.playerName(event.seat)
      const card = ctx.nameOf(event.cardId)
      if (card) return ctx.t('duel.log.draw', { name: who, card })
      return ctx.t('duel.log.drawHidden', { name: who })
    }
    case EventType.CARD_SUMMONED: {
      const who = ctx.playerName(event.seat)
      const card = cardOrFallback(ctx, event.cardId, event.cardUid)
      return event.tribute
        ? ctx.t('duel.log.tributeSummon', { name: who, card })
        : ctx.t('duel.log.summon', { name: who, card })
    }
    case EventType.CARD_SET:
      return ctx.t('duel.log.set', { name: ctx.playerName(event.seat) })
    case EventType.CARD_FLIPPED:
      if (!event.faceUp) return null
      return ctx.t('duel.log.flip', {
        card: cardOrFallback(ctx, event.cardId, event.cardUid),
      })
    case EventType.POSITION_CHANGED: {
      const card = ctx.uidName(event.cardUid) || ctx.t('duel.log.aCard')
      return event.position === CardPosition.ATTACK
        ? ctx.t('duel.log.posAtk', { card })
        : ctx.t('duel.log.posDef', { card })
    }
    case EventType.SPELL_ACTIVATED:
      return ctx.t('duel.log.spell', {
        name: ctx.playerName(event.seat),
        card: cardOrFallback(ctx, event.cardId, event.cardUid),
      })
    case EventType.TRAP_ACTIVATED:
      return ctx.t('duel.log.trap', {
        name: ctx.playerName(event.seat),
        card: cardOrFallback(ctx, event.cardId, event.cardUid),
      })
    case EventType.EFFECT_RESOLVED: {
      const card = ctx.uidName(event.cardUid) || ctx.t('duel.log.aCard')
      if (!event.detail) return null
      return ctx.t('duel.log.effect', { card })
    }
    case EventType.ATTACK_DECLARED: {
      const attacker = ctx.uidName(event.attackerUid) || ctx.t('duel.log.aCard')
      if (!event.targetUid) {
        return ctx.t('duel.log.attackDirect', { card: attacker })
      }
      const target = ctx.uidName(event.targetUid) || ctx.t('duel.log.aCard')
      return ctx.t('duel.log.attack', { card: attacker, target })
    }
    case EventType.ATTACK_REPLAY: {
      const attacker = ctx.uidName(event.attackerUid) || ctx.t('duel.log.aCard')
      return ctx.t('duel.log.replay', { card: attacker })
    }
    case EventType.CARD_DESTROYED:
      return ctx.t('duel.log.destroy', {
        card: ctx.uidName(event.cardUid) || ctx.t('duel.log.aCard'),
      })
    case EventType.CARD_BANISHED:
      return ctx.t('duel.log.banish', {
        card: ctx.uidName(event.cardUid) || ctx.t('duel.log.aCard'),
      })
    case EventType.CARD_TO_HAND:
      return ctx.t('duel.log.toHand', {
        card: ctx.uidName(event.cardUid) || ctx.t('duel.log.aCard'),
      })
    case EventType.LIFE_POINTS_CHANGED: {
      const delta =
        event.delta > 0 ? `+${event.delta}` : String(event.delta)
      return ctx.t('duel.log.lp', {
        name: ctx.playerName(event.seat),
        lp: event.lifePoints,
        delta,
      })
    }
    case EventType.GAME_OVER:
      return ctx.t('duel.log.gameOver', {
        name: ctx.playerName(event.winner),
      })
    case EventType.MESSAGE:
      return event.text || event.code ? localizeDuelMessage(event) : null
    default:
      return null
  }
}

/**
 * Turns a batch of engine events into log lines, dropping empty results.
 */
export function duelLogLinesFromEvents(
  events: GameEvent[],
  ctx: DuelLogCtx,
): DuelLogLine[] {
  const lines: DuelLogLine[] = []
  for (const event of events) {
    const text = describeOne(event, ctx)
    if (text) lines.push({ id: nextId(), text })
  }
  return lines
}
